"use client"

import * as React from "react"
import { Download, Loader2, Plus, Trash2 } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { SeccionPlegable } from "@/components/ui/seccion-plegable"
import { Input } from "@/components/ui/input"
import {
  comercialPhoneBindingsService,
  type ComercialPhoneBinding,
  type SeedReport,
} from "@/services/comercialPhoneBindingsService"

/**
 * De quién es cada móvil que escribe.
 *
 * No son números que se conecten -eso es el número de la empresa, en Ajustes-:
 * son los móviles de las personas. Cuando llega un mensaje, lo único que se
 * sabe es de qué número viene, y esta lista es lo que dice a quién pertenece.
 *
 * Un móvil que no está aquí no resuelve, así que no puede consultar nada. Un
 * comercial puede tener tantos como haga falta; un móvil es de uno solo.
 */
export function ComercialPhoneBindingsCard() {
  const [bindings, setBindings] = React.useState<ComercialPhoneBinding[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [savingId, setSavingId] = React.useState<string | null>(null)
  const [creating, setCreating] = React.useState(false)
  const [draft, setDraft] = React.useState({ phone: "", id_comercial: "", display_name: "" })
  const phoneInput = React.useRef<HTMLInputElement | null>(null)
  const [seeding, setSeeding] = React.useState(false)
  const [seedReport, setSeedReport] = React.useState<SeedReport | null>(null)

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      setBindings(await comercialPhoneBindingsService.list())
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void load()
  }, [load])

  /**
   * Traerse los teléfonos que iagestión ya conoce, en vez de teclear treinta y
   * tres. Primero en seco, para que quien lo pulsa vea qué va a pasar antes de
   * que pase; el segundo clic es el que escribe.
   */
  const seed = async (dryRun: boolean) => {
    setSeeding(true)
    try {
      const report = await comercialPhoneBindingsService.seed(dryRun)
      setSeedReport(report)
      if (!dryRun) await load()
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSeeding(false)
    }
  }

  const create = async () => {
    if (!draft.phone.trim() || !draft.id_comercial.trim()) return
    setCreating(true)
    try {
      await comercialPhoneBindingsService.create({
        phone: draft.phone.trim(),
        id_comercial: draft.id_comercial.trim(),
        display_name: draft.display_name.trim(),
      })
      setDraft({ phone: "", id_comercial: "", display_name: "" })
      await load()
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setCreating(false)
    }
  }

  const update = async (
    binding: ComercialPhoneBinding,
    changes: Partial<Pick<ComercialPhoneBinding, "id_comercial" | "display_name" | "is_active">>
  ) => {
    setSavingId(binding.id)
    try {
      await comercialPhoneBindingsService.update(binding.id, changes)
      await load()
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSavingId(null)
    }
  }

  const remove = async (binding: ComercialPhoneBinding) => {
    setSavingId(binding.id)
    try {
      await comercialPhoneBindingsService.remove(binding.id)
      await load()
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSavingId(null)
    }
  }

  return (
    <SeccionPlegable
      numero="07"
      etiqueta="Mark 2"
      titulo="Móviles de los comerciales"
      descripcion="Desde qué móvil escribe cada comercial. A quien no esté aquí, el agente no le puede consultar nada."
    >
      <div className="min-w-0 space-y-4">
        <p className="text-xs text-muted-foreground">
          No son números que se conecten —eso es el número de la empresa, en Ajustes—:
          son los móviles de las personas. Cuando llega un mensaje, esta lista es lo
          único que dice de quién es. Un comercial puede tener varios; un móvil es de
          uno solo.
        </p>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Cargando…</span>
          </div>
        ) : (
          <ul className="space-y-2">
            {bindings.map((binding) => (
              <li key={binding.id} className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2">
                <span className="w-40 shrink-0 font-mono text-sm">{binding.phone}</span>

                <Input
                  className="h-8 w-44"
                  defaultValue={binding.display_name}
                  placeholder="Sin nombre"
                  disabled={savingId === binding.id}
                  onBlur={(event) => {
                    const value = event.target.value.trim()
                    if (value !== binding.display_name) void update(binding, { display_name: value })
                  }}
                />

                <Input
                  className="h-8 w-32"
                  defaultValue={binding.id_comercial}
                  placeholder="IdComercial"
                  disabled={savingId === binding.id}
                  onBlur={(event) => {
                    const value = event.target.value.trim()
                    if (value && value !== binding.id_comercial) {
                      void update(binding, { id_comercial: value })
                    }
                  }}
                />

                {binding.seeded_from_census ? (
                  <Badge variant="outline">Del censo</Badge>
                ) : (
                  <Badge variant="secondary">A mano</Badge>
                )}

                {!binding.is_active && <Badge variant="outline">Inactivo</Badge>}

                <Button
                  variant="ghost"
                  size="sm"
                  disabled={savingId === binding.id}
                  onClick={() => {
                    // Anadir otro movil de este mismo comercial: se rellena su
                    // IdComercial para no tener que buscarlo ni copiarlo.
                    setDraft({
                      phone: "",
                      id_comercial: binding.id_comercial,
                      display_name: binding.display_name,
                    })
                    phoneInput.current?.focus()
                  }}
                  aria-label={`Añadir otro móvil de ${binding.display_name || binding.phone}`}
                  title="Añadir otro móvil de este comercial"
                >
                  <Plus className="h-4 w-4" />
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  disabled={savingId === binding.id}
                  onClick={() => void remove(binding)}
                  aria-label={`Retirar ${binding.phone}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t pt-3">
          <Input
            ref={phoneInput}
            className="h-8 w-44"
            placeholder="+34 600 00 00 00"
            value={draft.phone}
            onChange={(event) => setDraft((d) => ({ ...d, phone: event.target.value }))}
          />
          <Input
            className="h-8 w-32"
            placeholder="IdComercial"
            value={draft.id_comercial}
            onChange={(event) => setDraft((d) => ({ ...d, id_comercial: event.target.value }))}
          />
          <Input
            className="h-8 w-44"
            placeholder="Nombre (opcional)"
            value={draft.display_name}
            onChange={(event) => setDraft((d) => ({ ...d, display_name: event.target.value }))}
          />
          <Button size="sm" onClick={() => void create()} disabled={creating}>
            <Plus className="mr-1 h-4 w-4" />
            Declarar
          </Button>
        </div>

        <div className="space-y-2 border-t pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" disabled={seeding} onClick={() => void seed(true)}>
              {seeding ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-1 h-4 w-4" />
              )}
              Traer los del censo
            </Button>
            {seedReport?.dry_run && (
              <Button size="sm" disabled={seeding} onClick={() => void seed(false)}>
                Confirmar
              </Button>
            )}
            <span className="text-xs text-muted-foreground">
              iagestión ya tiene el móvil de cada comercial; esto los copia en vez de
              teclearlos.
            </span>
          </div>

          {seedReport && (
            <p className="text-xs text-muted-foreground">
              {seedReport.dry_run ? "Traería " : "Ha traído "}
              {seedReport.creados} nuevos, {seedReport.actualizados} actualizados y deja{" "}
              {seedReport.respetados} como están por estar escritos a mano.
            </p>
          )}

          {seedReport && seedReport.ambiguos.length > 0 && (
            <div className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs">
              <p className="font-medium">
                {seedReport.ambiguos.length} teléfono(s) figuran en más de un comercial y no
                se declaran solos. Decláralos aquí abajo diciendo de quién son:
              </p>
              <ul className="mt-1 space-y-1">
                {seedReport.ambiguos.map((row) => (
                  <li key={row.telefono} className="font-mono">
                    …{row.telefono} → {row.comerciales.join(", ")}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          El <strong>+</strong> de cada fila añade otro móvil a ese mismo comercial —el
          personal, el segundo— y le rellena su IdComercial. Lo que añadas o edites a
          mano queda marcado como tal, y traer del censo no lo pisa nunca. Un móvil de
          pruebas se mueve de comercial cambiando su IdComercial.
        </p>
      </div>
    </SeccionPlegable>
  )
}
